import { useState, useEffect } from "react";
import ReactSignatureCanvas from "react-signature-canvas";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { FileText } from "lucide-react";

interface ContractModalProps {
  open: boolean;
  onClose: () => void;
  sigCanvasRef:any;
  submitSignature:any;
  project:any;
  pdfURL:any;
  title:any
  submitting?: boolean;
  submitLabel?: string;
  submittingLabel?: string;
}

export function ContractLoadingModal({
  title,
  open,
  pdfURL,
  submitSignature,
  sigCanvasRef,
  onClose,
  submitting = false,
  submitLabel = "Submit Signed Contract",
  submittingLabel = "Signing & updating PDF...",
}: ContractModalProps) {
  const [hasReviewed, setHasReviewed] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (open) {
      setHasReviewed(false);
      setHasSignature(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleResize = () => setHasSignature(false);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open]);

  const handleClearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
    setHasSignature(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="tailwind-prefix contract-loading-dialog">
        <div className="contract-signing-dialog__header">
          <div className="contract-signing-dialog__heading-icon" aria-hidden="true">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 pr-8">
            <DialogTitle className="contract-signing-dialog__title">{title}</DialogTitle>
            <DialogDescription className="contract-signing-dialog__description">
              Review the complete document, confirm your agreement, then add your signature.
            </DialogDescription>
          </div>
        </div>

        <div className="contract-signing-dialog__body">
          <section className="contract-signing-dialog__document" aria-label="Contract document preview">
            <div className="contract-signing-dialog__pdf-frame">
              {pdfURL ? (
                <iframe
                  src={pdfURL}
                  className="h-full w-full"
                  title="Contract PDF Preview"
                />
              ) : (
                <div className="contract-signing-dialog__empty-preview">
                  <FileText className="h-6 w-6" aria-hidden="true" />
                  No PDF available
                </div>
              )}
            </div>
          </section>

          <label htmlFor="reviewed" className={`contract-signing-dialog__consent ${hasReviewed ? "is-checked" : ""}`}>
            <input
              type="checkbox"
              id="reviewed"
              checked={hasReviewed}
              className="contract-signing-dialog__checkbox"
              onChange={(e) => setHasReviewed(e.target.checked as boolean)}
            />
            <Label
              htmlFor="reviewed"
              className="contract-signing-dialog__consent-label"
            >
              I have reviewed the complete document and agree to its terms
            </Label>
          </label>

          <section className="contract-signing-dialog__signature" aria-labelledby="contract-signature-heading">
            <div className="contract-signing-dialog__signature-header">
              <div>
                <Label id="contract-signature-heading" className="contract-signing-dialog__signature-title">Your Signature</Label>
                <p className="contract-signing-dialog__signature-help">Use your mouse, trackpad, or touchscreen to sign.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearSignature}
                disabled={!hasSignature || submitting}
              >
                Clear
              </Button>
            </div>
            <div className="contract-signing-dialog__signature-pad">
              <ReactSignatureCanvas
                ref={sigCanvasRef}
                canvasProps={{
                  className: "contract-signing-dialog__signature-canvas",
                }}
                backgroundColor="white"
                onEnd={() => setHasSignature(true)}
              />
            </div>
          </section>
        </div>
        <div className="contract-signing-dialog__footer">
          <div className="contract-signing-dialog__actions">
            <Button variant="outline" className="w-full sm:w-auto" disabled={submitting} onClick={onClose}>
              Cancel
            </Button>
            <Button className="w-full sm:w-auto" disabled={!hasReviewed || !hasSignature || submitting} onClick={submitSignature}>
              {submitting ? submittingLabel : submitLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
